import type { FormEmail, FormName, FormPhone } from './validationSchema';
import type { BoxProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Modal, TextField, Tooltip, Typography, styled, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { nanoid } from 'nanoid';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Button } from '@components/index';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';
import { OTPIcon } from '@icons/OTPIcon';
import { PencilIcon } from '@icons/PencilIcon';
import { PhoneIcon } from '@icons/PhoneIcon';
import { NameIcon, OrganizationIcon, GroupIcon, MailIcon, KeyIcon, XIcon } from '@icons/index';
import type { ProfileUpdateRq } from '@repositories/userRepository/user.types';
import { useGetUserProfile, useUpdateUserProfile } from '@queryHooks/useUser';
import i18n from '@locales/i18n';
import { MFASettingModal } from './MFASettingModal';
import { emailFormOptions, nameFormOptions, phoneFormOptions } from './validationSchema';

type EditMode = {
  phone: boolean;
  name: boolean;
  email: boolean;
};

type ModalWrapperProps = BoxProps & {
  language: string;
};

export const MyProfileModal = observer(() => {
  const { t } = useTranslation();
  const {
    uiStore: { profileModalStore: modal },
    authStore,
  } = useStore();

  const naviage = useNavigate();

  const theme = useTheme();

  const [language, setLanguage] = useState<string>(i18n.language);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);

  const [isEditMode, setIsEditMode] = useState<EditMode>({ phone: false, name: false, email: false });
  const {
    register: registerPhone,
    handleSubmit: handleSubmitPhone,
    reset: resetPhone,
    formState: { errors: phoneErrors },
  } = useForm<FormPhone>(phoneFormOptions);

  const {
    register: registerName,
    handleSubmit: handleSubmitName,
    reset: resetName,
    formState: { errors: nameErrors },
  } = useForm<FormName>(nameFormOptions);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    reset: resetEmail,
    formState: { errors: emailErrors },
  } = useForm<FormEmail>(emailFormOptions);

  const {
    uiStore: { toastStore },
  } = useStore();
  const { data: user } = useGetUserProfile(authStore.id);
  const { mutate: updateUser, isPending: isUpdateUserPending } = useUpdateUserProfile();

  const useMfaOption = user?.mfaEnabled ?? false;

  const validation = {
    successPhone: (data: FormPhone) => {
      if (user) {
        const rqData: ProfileUpdateRq & { id: string } = { name: user.name, phone: data.phone ?? '', id: user.id };
        updateUser(rqData, {
          onSuccess: () => {
            setIsEditMode(prev => {
              return { ...prev, phone: false };
            });
            toastStore.open({ message: t('GNB_Profile_MyProfile_18'), hideAction: true });
          },
        });
      }
    },
    successName: (data: FormName) => {
      if (user) {
        const rqData: ProfileUpdateRq & { id: string } = { name: data.name, email: user.email, id: user.id };
        updateUser(rqData, {
          onSuccess: () => {
            setIsEditMode(prev => {
              return { ...prev, name: false };
            });
            toastStore.open({ message: t('GNB_Profile_MyProfile_17'), hideAction: true });
          },
        });
      }
    },
    successEmail: (data: FormEmail) => {
      if (user) {
        const rqData: ProfileUpdateRq & { id: string } = { name: user.name, email: data.email, id: user.id };
        updateUser(rqData, {
          onSuccess: () => {
            setIsEditMode(prev => {
              return { ...prev, email: false };
            });
            toastStore.open({ message: t('GNB_Profile_MyProfile_16'), hideAction: true });
          },
        });
      }
    },
  };

  const handleToggleMfaModal = () => {
    setIsMfaModalOpen(prev => !prev);
  };

  useEffect(() => {
    setLanguage(i18n.language);
  }, []);

  return (
    <>
      <Modal open={modal.visible}>
        <ModalWrapper language={language}>
          <Header>
            <Typography variant="headline6" component="strong">
              {t('GNB_Profile_Menu_01')}
            </Typography>
            <Button variant="text" onClick={modal.close}>
              <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
            </Button>
          </Header>
          <Body className={isEditMode ? 'edit-mode' : ''}>
            <Property>
              <Key>
                <OrganizationIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Member_LNB_01')}</span>
              </Key>
              <Value>
                <span>{user?.company[0]?.name}</span>
              </Value>
            </Property>
            <Property>
              <Key>
                <MailIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Member_User_14')}</span>
              </Key>
              <Value>
                <span>{user?.username}</span>
              </Value>
            </Property>
            <Property className={nameErrors.name?.message ? 'edit-mode' : ''}>
              <Key>
                <NameIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Workflow_Main_05')}</span>
              </Key>
              {isEditMode.name ? (
                <form onSubmit={handleSubmitName(validation.successName)}>
                  <Value>
                    <TextField
                      {...registerName('name')}
                      size="small"
                      fullWidth
                      error={!!nameErrors.name?.message}
                      helperText={nameErrors.name?.message}
                      autoFocus={isEditMode.name}
                    />
                    <Buttons>
                      <StyledOutlineBtn
                        variant="outline"
                        onClick={() => {
                          resetName({ name: user?.name || '' });
                          setIsEditMode(prev => {
                            return { ...prev, name: false };
                          });
                        }}
                        size="small"
                        paddingHorizontal={12}
                      >
                        {t('Subscrib_Detail_BuyRequest_13')}
                      </StyledOutlineBtn>
                      <SecureSubmitButton variant="contain" size="small" type="submit" paddingHorizontal={12} isPending={isUpdateUserPending}>
                        {t('Member_User_24')}
                      </SecureSubmitButton>
                    </Buttons>
                  </Value>
                </form>
              ) : (
                <Value>
                  <span>{user?.name}</span>
                  <IconButton
                    onClick={() => {
                      resetName({ name: user?.name || '' });
                      setIsEditMode(prev => {
                        return { ...prev, name: true };
                      });
                    }}
                    variant="outline"
                    size="small"
                  >
                    <PencilIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
                  </IconButton>
                </Value>
              )}
            </Property>
            <Property>
              <Key>
                <GroupIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Member_User_Bulk_Create_18')}</span>
              </Key>
              <Value>
                {(user?.userGroups.length as number) > 1 ? (
                  <Groups>
                    <span>{user?.userGroups[0].name}&nbsp;</span>
                    <Tooltip
                      title={user?.userGroups.map((usergroup, idx) => {
                        if (idx > 0) return <UsergroupOfTooltip key={nanoid()}>{usergroup.name}</UsergroupOfTooltip>;
                        return null;
                      })}
                      arrow
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            width: '210px',
                            border: `1px solid ${theme.colors['border-gray-lighter']}`,
                            backgroundColor: theme.colors['bg-white'],
                            padding: '6px',
                            marginTop: '4px !important',
                          },
                          '& .MuiTooltip-arrow': {
                            color: theme.colors['text-white'],
                            '&::before': {
                              border: `1px solid ${theme.colors['border-gray-lighter']}`,
                            },
                          },
                        },
                      }}
                    >
                      <RestUsergroups>
                        {t('GNB_Profile_MyProfile_06', { number: (user?.userGroups.length as number) - 1 })}
                      </RestUsergroups>
                    </Tooltip>
                  </Groups>
                ) : (
                  <span>{user?.userGroups[0]?.name}</span>
                )}
              </Value>
            </Property>
            <Property className={phoneErrors.phone?.message ? 'edit-mode' : ''}>
              <Key>
                <PhoneIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Member_User_16')}</span>
              </Key>
              {isEditMode.phone ? (
                <form onSubmit={handleSubmitPhone(validation.successPhone)}>
                  <Value>
                    <TextField
                      {...registerPhone('phone')}
                      size="small"
                      fullWidth
                      error={!!phoneErrors.phone?.message}
                      helperText={phoneErrors.phone?.message}
                      autoFocus={isEditMode.phone}
                    />
                    <Buttons>
                      <StyledOutlineBtn
                        variant="outline"
                        onClick={() => {
                          resetPhone({ phone: user?.phone || '' });
                          setIsEditMode(prev => {
                            return { ...prev, phone: false };
                          });
                        }}
                        size="small"
                        paddingHorizontal={12}
                      >
                        {t('Subscrib_Detail_BuyRequest_13')}
                      </StyledOutlineBtn>
                      <SecureSubmitButton variant="contain" size="small" type="submit" paddingHorizontal={12} isPending={isUpdateUserPending}>
                        {t('Member_User_24')}
                      </SecureSubmitButton>
                    </Buttons>
                  </Value>
                </form>
              ) : (
                <Value>
                  <span>{user?.phone || '-'}</span>
                  <IconButton
                    onClick={() => {
                      resetPhone({ phone: user?.phone || '' });
                      setIsEditMode(prev => {
                        return { ...prev, phone: true };
                      });
                    }}
                    variant="outline"
                    size="small"
                  >
                    <PencilIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
                  </IconButton>
                </Value>
              )}
            </Property>
            <Property className={nameErrors.name?.message ? 'edit-mode' : ''}>
              <Key>
                <NameIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('Member_User_30')}</span>
              </Key>
              {isEditMode.email ? (
                <form onSubmit={handleSubmitEmail(validation.successEmail)}>
                  <Value>
                    <TextField
                      {...registerEmail('email')}
                      size="small"
                      fullWidth
                      error={!!emailErrors.email?.message}
                      helperText={emailErrors.email?.message}
                      autoFocus={isEditMode.email}
                    />
                    <Buttons>
                      <StyledOutlineBtn
                        variant="outline"
                        onClick={() => {
                          resetEmail({ email: user?.email || '' });
                          setIsEditMode(prev => {
                            return { ...prev, email: false };
                          });
                        }}
                        size="small"
                        paddingHorizontal={12}
                      >
                        {t('Subscrib_Detail_BuyRequest_13')}
                      </StyledOutlineBtn>
                      <SecureSubmitButton variant="contain" size="small" type="submit" paddingHorizontal={12} isPending={isUpdateUserPending}>
                        {t('Member_User_24')}
                      </SecureSubmitButton>
                    </Buttons>
                  </Value>
                </form>
              ) : (
                <Value>
                  <span>{user?.email}</span>
                  <IconButton
                    onClick={() => {
                      resetEmail({ email: user?.email || '' });
                      setIsEditMode(prev => {
                        return { ...prev, email: true };
                      });
                    }}
                    variant="outline"
                    size="small"
                  >
                    <PencilIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
                  </IconButton>
                </Value>
              )}
            </Property>
            <Property>
              <Key>
                <KeyIcon color={theme.colors['ic-gray-light']} width={18} height={18} />
                <span>{t('GNB_Profile_MyProfile_07')}</span>
              </Key>
              <Value>
                <span>
                  {t('GNB_Profile_MyProfile_10', {
                    Day: dayjs(user?.lastPasswordChangeDate).isValid()
                      ? dayjs(user?.lastPasswordChangeDate).format(t('DateFormat_YMD'))
                      : '',
                  })}
                </span>
                <Button
                  size="small"
                  variant="outline"
                  onClick={() => {
                    modal.close();
                    naviage('/change-password');
                  }}
                  paddingHorizontal={12}
                >
                  {t('GNB_Profile_MyProfile_09')}
                </Button>
              </Value>
            </Property>
            <Hr />
            <MfaSection>
              <MfaTitleRow>
                <MfaTitleLeft>
                  <OTPIcon color={theme.colors['ic-gray-light']} width={16} height={16} />
                  <MfaTextGroup>
                    <MfaTitle>{t('Mfa.profile.title')}</MfaTitle>
                    <MfaDescription>
                      {useMfaOption
                        ? t('Mfa.profile.createdDate', {
                            date: user?.mfaSetupAt ? dayjs(user?.mfaSetupAt).format(t('DateFormat_YMD_Time_HM')) : '',
                          })
                        : t('Mfa.profile.description')}
                    </MfaDescription>
                  </MfaTextGroup>
                </MfaTitleLeft>
                <MfaLabel useMfaOption={useMfaOption}>{useMfaOption ? 'ON' : 'OFF'}</MfaLabel>
              </MfaTitleRow>
              <MfaCard>
                <MfaCardContent>
                  <MfaCardText>
                    <MfaCardTitle>OTP</MfaCardTitle>
                    <MfaCardStatus>
                      <CheckCircleIcon
                        color={useMfaOption ? theme.colors['ic-green'] : theme.colors['ic-gray-lighter']}
                      />
                      <MfaCardStatusText
                        color={useMfaOption ? theme.colors['text-green'] : theme.colors['text-gray-light']}
                      >
                        {useMfaOption ? t('Mfa.profile.configured') : t('Mfa.profile.notConfigured')}
                      </MfaCardStatusText>
                    </MfaCardStatus>
                  </MfaCardText>
                  <Button
                    size="small"
                    variant="outline"
                    paddingHorizontal={16}
                    disabled={!useMfaOption}
                    onClick={handleToggleMfaModal}
                  >
                    {t('Mfa.profile.viewDetails')}
                  </Button>
                </MfaCardContent>
              </MfaCard>
            </MfaSection>
          </Body>
        </ModalWrapper>
      </Modal>

      <MFASettingModal
        open={isMfaModalOpen}
        onClose={handleToggleMfaModal}
        mfaEnabled={!!useMfaOption}
        mfaSetupDate={
          !!useMfaOption && user?.mfaSetupAt ? dayjs(user?.mfaSetupAt).format(t('DateFormat_YMD_Time_HM')) : ''
        }
        mfaStep="DETAIL_VIEW"
      />
    </>
  );
});

const ModalWrapper = styled(Box)<ModalWrapperProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => (props.language === 'ko' ? '600px' : '653px')};
  min-height: 460px;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  border-radius: 6px;
  box-shadow: 0px 6px 30px 5px rgba(0, 0, 0, 0.05), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 8px 10px -5px rgba(0, 0, 0, 0.08);
  overflow: auto;
`;

const Header = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: inherit;
  padding: 18px 20px 16px 28px;
`;

const Body = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 34px 48px 64px 60px;
  align-items: flex-start;
  justify-content: center;
  &.edit-mode {
    padding: 34px 48px 0 40px;
  }
`;

const Property = styled(Box)`
  width: 100%;
  min-height: 24px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  & > form {
    width: 100%;
  }
  margin-bottom: 16px;
  &:last-of-type {
    margin-bottom: 0;
  }
  &.edit-mode {
    margin-bottom: 12px;
  }
`;

const Key = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 157px;
  flex-shrink: 0;
  align-items: center;

  & > span {
    ${fonts.Body1}
    color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
    padding: 4px 0;
  }
`;

const Value = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 8px;

  & > span {
    ${fonts.Body1}
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    padding: 4px 0;
  }
  & .MuiFormControl-root {
    max-width: 225px;
    height: auto;
  }
  // & p.MuiFormHelperText-root {
  //   margin: 4px 0 12px;
  // }
`;

const Buttons = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const IconButton = styled(Button)`
  width: 32px;
  border-color: ${({ theme }) => theme.colors['border-gray-light']};
  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
    & svg path {
      fill: ${({ theme }) => theme.colors['ic-primary']};
    }
  }
`;

const Groups = styled(Box)`
  display: flex;
`;

const UsergroupOfTooltip = styled('span')`
  display: flex;
  align-items: center;
  ${fonts.Body1}
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  padding: 8px 7px;
`;

const RestUsergroups = styled(Box)`
  ${fonts.Underline1}
  color: ${({ theme: { colors } }) => colors['text-blue-light']};
  text-decoration-color: ${({ theme: { colors } }) => colors['text-blue-light']};
`;

const StyledOutlineBtn = styled(Button)`
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
`;

const Hr = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
`;

const MfaSection = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MfaTitleRow = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const MfaTitleLeft = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;

  svg {
    margin-top: 10px;
  }
`;

const MfaTextGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const MfaTitle = styled(Typography)`
  ${fonts.Body1}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const MfaDescription = styled(Typography)`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const MfaLabel = styled(Box)<{ useMfaOption: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 4px;
  border: 0.5px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 2px;
  gap: 10px;
  ${fonts.Caption4}
  color: ${({ useMfaOption, theme }) =>
    useMfaOption ? theme.colors['text-white'] : theme.colors['text-gray-sub-darker']};
  background-color: ${({ useMfaOption, theme }) =>
    useMfaOption ? theme.colors['bg-green'] : theme.colors['bg-gray-light']};
`;

const MfaCard = styled(Box)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 4px;
  padding: 12px 12px 12px 26px;
  margin-bottom: 32px;
`;

const MfaCardContent = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const MfaCardText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const MfaCardTitle = styled(Typography)`
  ${fonts.Body1}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  width: 110px;
` as typeof Typography;

const MfaCardStatus = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const MfaCardStatusText = styled('span')<{ color: string }>`
  ${fonts.Body3}
  color: ${({ color }) => color};
`;
